import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Content, Header, Page } from '@backstage/core-components';
import ExploreIcon from '@material-ui/icons/Explore';
import SlideshowIcon from '@material-ui/icons/Slideshow';
import RestaurantIcon from '@material-ui/icons/Restaurant';
import GroupWorkIcon from '@material-ui/icons/GroupWork';
import LinkIcon from '@material-ui/icons/Link';
import EventIcon from '@material-ui/icons/Event';
import { FaDocker, FaChalkboardTeacher, FaCubes, FaRobot, FaIdCard, FaMicrochip, FaTasks, FaCode } from 'react-icons/fa';
import { SiKubernetes, SiGitlab } from 'react-icons/si';
import { GiBrain } from 'react-icons/gi';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { useNavigate, useParams } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    cursor: 'pointer',
    transition: 'box-shadow 0.2s',
    '&:hover': {
      boxShadow: theme.shadows[6],
    },
  },
  cardContent: {
    flexGrow: 1,
    textAlign: 'center',
    padding: theme.spacing(4),
  },
  icon: {
    fontSize: 56,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
  },
  cardActions: {
    justifyContent: 'center',
    paddingBottom: theme.spacing(3),
  },
  backButton: {
    marginBottom: theme.spacing(3),
  },
}));

export type SystemConfig = {
  name: string;
  systemId: string;
  description: string;
  Icon: React.ComponentType<{ className?: string }>;
  externalUrl?: string;
};

export type DomainConfig = {
  title: string;
  subtitle: string;
  systems: SystemConfig[];
};

export const domainConfig: Record<string, DomainConfig> = {
  academy: {
    title: 'Formations',
    subtitle: 'Support de formations dispensées et validées',
    systems: [
      {
        name: 'Cloud & IT',
        systemId: 'academy-cloud-it',
        description: 'Formations Kubernetes et Docker',
        Icon: SiKubernetes,
      },
      {
        name: 'Software',
        systemId: 'academy-software',
        description: 'Formation Langages et Frameworks',
        Icon: FaCode,
      },
      {
        name: 'IA',
        systemId: 'academy-ia',
        description: 'Formations Intelligence Artificielle et GenAI',
        Icon: GiBrain,
      },
      {
        name: 'IOT & Embarqué',
        systemId: 'academy-iot-embarque',
        description: 'Formations IOT et systèmes embarqués',
        Icon: FaMicrochip,
      },
      {
        name: 'Management & Projets',
        systemId: 'academy-management-projets',
        description: 'Formations méthodologies et gestion de projet',
        Icon: FaTasks,
      },
    ],
  },
  innersource: {
    title: 'Innersource',
    subtitle: 'Projets partagés et outils internes',
    systems: [
      {
        name: 'Ready to Use',
        systemId: 'ready-to-use',
        description: 'Ready-to-use Docker images and Helm charts',
        Icon: FaDocker,
      },
      {
        name: 'Forge Platform',
        systemId: 'forge-platform',
        description: 'Plateforme GitLab Forge',
        Icon: SiGitlab,
      },
      {
        name: 'Bots & Automation',
        systemId: 'bots-automation',
        description: 'Bots et automatisations de la Forge (GitLab Agent, Renovate)',
        Icon: FaRobot,
      },
      {
        name: 'CVeloce',
        systemId: 'cv-management',
        description: 'Suite d\'outils de gestion des CVs (CVeloce)',
        Icon: FaIdCard,
      },
      {
        name: 'Kadence',
        systemId: 'kadence',
        description: 'Application de gestion de projet',
        Icon: FaTasks,
      },
      {
        name: 'Team',
        systemId: 'kzt',
        description: "Planification et partage d'événements internes",
        Icon: EventIcon,
      },
    ],
  },
  'coordination-technique': {
    title: 'Coordination Technique',
    subtitle: 'Outils et instances de coordination technique inter-pôles',
    systems: [
      {
        name: 'Tech Radar',
        systemId: 'tech-radar-kzs',
        description: 'Radar Technologique Interactif',
        Icon: ExploreIcon,
      },
      {
        name: 'Backstage',
        systemId: 'backstage-kzs',
        description: 'Source de la plateforme Backstage',
        Icon: FaCubes,
      },
      {
        name: 'Pôles de compétences',
        systemId: 'poles-competences-sharepoint',
        description: 'Espace SharePoint des pôles de compétences',
        Icon: LinkIcon,
        externalUrl: 'https://kaizen38240.sharepoint.com/sites/poles-de-competences',
      },
    ],
  },
  'knowledge-sharing': {
    title: 'Partage de connaissances',
    subtitle:
      'Talks, ateliers, templates et exemples partagés par la communauté',
    systems: [
      {
        name: 'Templates Présentation',
        systemId: 'templates-presentation',
        description: 'Templates et ressources de présentation',
        Icon: SlideshowIcon,
      },
      {
        name: 'Présentations Lunch',
        systemId: 'presentation-lunch',
        description: 'Présentations Lunch & Learn',
        Icon: RestaurantIcon,
      },
      {
        name: 'Présentations Techniques Lab',
        systemId: 'prez-technique-lab',
        description: "Sessions du lab d'innovation, contexte d'idéation projet",
        Icon: FaChalkboardTeacher,
      },
      {
        name: 'Ateliers',
        systemId: 'presentation-ateliers',
        description: 'Ateliers de partage de connaissances internes',
        Icon: GroupWorkIcon,
      },
      {
        name: 'Code en action',
        systemId: 'code-en-action',
        description: "Exemples d'implémentation et ressources techniques",
        Icon: FaCode,
      },
    ],
  },
};

export const DomainPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { domainName } = useParams<{ domainName: string }>();

  const config = domainName ? domainConfig[domainName] : undefined;

  if (!config) {
    return (
      <Page themeId="home">
        <Header title="Domaine introuvable" />
        <Content>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
          >
            Retour à l'accueil
          </Button>
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="home">
      <Header title={config.title} subtitle={config.subtitle} />
      <Content>
        <div className={classes.backButton}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')}>
            Retour
          </Button>
        </div>
        <Grid container spacing={4} justifyContent="center">
          {config.systems.map(({ name, systemId, description, Icon, externalUrl }) => {
            const goToSystem = () =>
              externalUrl
                ? window.open(externalUrl, '_blank', 'noopener,noreferrer')
                : navigate(`/system/${systemId}`, { state: { title: name } });

            return (
              <Grid item xs={12} sm={6} md={4} key={systemId}>
                <Card className={classes.card} onClick={goToSystem} elevation={2}>
                  <CardContent className={classes.cardContent}>
                    <Icon className={classes.icon} />
                    <Typography variant="h5" gutterBottom>
                      {name}
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                      {description}
                    </Typography>
                  </CardContent>
                  <CardActions className={classes.cardActions}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={e => {
                        e.stopPropagation();
                        goToSystem();
                      }}
                    >
                      Explorer
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Content>
    </Page>
  );
};
